import { ErrorHandler, Injectable, inject, NgZone } from '@angular/core';
import { NotificationService } from './notification.service';

const STALE_CHUNK_RELOAD_KEY = 'assessment_stale_chunk_reload_at';
const STALE_CHUNK_RELOAD_PARAM = 'appReload';
const STALE_CHUNK_RELOAD_WINDOW_MS = 30_000;

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'failed to import module script',
  'importing a module script failed',
  'chunkloaderror',
];

export function isStaleChunkLoadError(error: unknown): boolean {
  const message = extractErrorMessages(error).join(' ').toLowerCase();
  return (
    DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => message.includes(pattern)) ||
    (message.includes('loading chunk') && message.includes('failed'))
  );
}

function extractErrorMessages(error: unknown, seen = new Set<unknown>()): string[] {
  if (error === null || error === undefined || seen.has(error)) {
    return [];
  }
  seen.add(error);

  if (typeof error === 'string') {
    return [error];
  }

  if (error instanceof Error) {
    return [error.name, error.message, ...extractErrorMessages(error.cause, seen)];
  }

  if (typeof error !== 'object') {
    return [String(error)];
  }

  const record = error as Record<string, unknown>;
  return ['message', 'reason', 'rejection', 'error', 'cause'].flatMap((key) =>
    extractErrorMessages(record[key], seen)
  );
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly notification = inject(NotificationService);
  private readonly zone = inject(NgZone);

  handleError(error: unknown): void {
    // eslint-disable-next-line no-console
    console.error('Unhandled error:', error);

    if (isStaleChunkLoadError(error) && this.reloadForFreshAppBuild()) {
      return;
    }

    this.zone.run(() => {
      this.notification.showError('An unexpected error occurred. Please try again.');
    });
  }

  private reloadForFreshAppBuild(): boolean {
    const now = Date.now();
    if (this.hasRecentReloadAttempt(now)) {
      return false;
    }

    this.markReloadAttempt(now);

    try {
      const url = new URL(window.location.href);
      url.searchParams.set(STALE_CHUNK_RELOAD_PARAM, String(now));
      window.location.assign(url.toString());
      return true;
    } catch {
      return false;
    }
  }

  private hasRecentReloadAttempt(now: number): boolean {
    try {
      const previousAttempt = Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY));
      return (
        Number.isFinite(previousAttempt) && now - previousAttempt < STALE_CHUNK_RELOAD_WINDOW_MS
      );
    } catch {
      return false;
    }
  }

  private markReloadAttempt(now: number): void {
    try {
      sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
    } catch {
      // If storage is unavailable, the URL reload still gives the app one recovery attempt.
    }
  }
}
